import type {
  GradeGroup,
  LanguageTheme,
  WorksheetGenericLanguageModule,
  WorksheetModule,
  WorksheetTheme,
} from "@arbeidsark/shared"
import { legalReadingTextThemes } from "@arbeidsark/shared"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Document, Page } from "react-pdf"
import { Button, Form, FormField, Input, Label, SVGIcon } from "~/components"
import NumberInput from "~/components/Input/NumberInput"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { trpc } from "~/utils/trpc"
import { validateNonEmpty } from "~/utils/validation"
import {
  captureWorksheetConfigChange,
  captureWorksheetDownload,
  captureWorksheetSaved,
} from "../analytics"
import {
  FloatingActions,
  GeneratorWrapper,
  PdfScrollArea,
  PreviewArea,
  Sidebar,
  SidebarContent,
} from "../components/GeneratorLayout"
import GeneratorMainFrame from "../components/GeneratorMainFrame"
import GenericLanguagePageConfigurationList from "../components/pageConfigurations/generic/GenericLanguagePageConfigurationList"
import GradeLevelGroupSelect from "../components/selects/GradeLevelGroupSelect"
import LanguageThemeSelect from "../components/selects/LanguageThemeSelect"
import LayoutSelect from "../components/selects/LayoutSelect"
import NorwegianTaskTypesSelect from "../components/selects/NorwegianTaskTypesMultiSelect"
import ThemeSelect from "../components/selects/ThemeSelect"
import { useGeneratorContext } from "../context/GeneratorContext"
import { downloadWorksheet, normalizePreviewPages } from "../util"

export type NorwegianGeneratorProps = {}

export interface NorwegianGeneratorFormData {
  name: string
  gradeGroup: GradeGroup
  languageTheme: LanguageTheme
  layout: "half" | "full"
  taskTypes: WorksheetGenericLanguageModule[]
  pages: number
  theme: WorksheetTheme
  moduleConfigurations: Record<string, Record<string, WorksheetModule>>
}

const NorwegianGenerator: React.FC<NorwegianGeneratorProps> = () => {
  const { worksheetName, setWorksheetName } = useGeneratorContext()

  const translations = useTranslate({
    name: "common.name",
    grade: "common.school-grade",
    operation: "common.operation",
    generate: "worksheets.buttons.generate-worksheet",
    regenerate: "worksheets.buttons.regenerate",
    save: "common.save",
    download: "common.download",
    layout: "worksheets.labels.page-layout",
    taskTypes: "worksheets.labels.select-task-types",
    numberOfPages: "worksheets.labels.number-of-pages",
    languageTheme: "worksheets.labels.language-theme",
    theme: "worksheets.labels.select-theme",

    newWorksheet: "common.new-worksheet",

    loading: "common.loading...",
    errors: {
      nonEmpty: "errors.generic-not-empty-field",
      readingTextsNeedFullLayout:
        "errors.worksheets.readingtexts-need-full-layout",
    },
  })

  const {
    formValues,
    formChangeHandler,
    formErrors,
    submitHandler,
    updateForm,
    formValid,
  } = useForm<NorwegianGeneratorFormData>({
    values: {
      name: worksheetName,
      gradeGroup: "1-4",
      languageTheme: "birds",
      layout: "full",
      taskTypes: [],
      pages: 2,
      theme: "numberchars",
      moduleConfigurations: {},
    },
    validators: {
      name: validateNonEmpty(translations.errors.nonEmpty),
      taskTypes: (value, form) => {
        if (value.includes("generic/readingTexts") && form.layout === "half") {
          return translations.errors.readingTextsNeedFullLayout
        }
        return null
      },
      moduleConfigurations: (value, form) => {
        const errors = Object.values(value)
          .flatMap((moduleConfig) => {
            return Object.values(moduleConfig).map((module) => {
              if (
                module.type === "generic/readingTexts" &&
                form.layout === "half"
              ) {
                return translations.errors.readingTextsNeedFullLayout
              }
              return null
            })
          })
          .filter((x) => x !== null)

        if (errors.length > 0) {
          return errors.join("")
        }

        return null
      },
    },
  })

  // Sync name changes to the shared context
  useEffect(() => {
    setWorksheetName(formValues.name)
  }, [formValues.name, setWorksheetName])

  const toast = useToast()
  const { getToastInfo } = useErrorHandling()

  const [resolvedModules, setResolvedModules] = useState<
    WorksheetModule[][] | null
  >(null)
  const [pdfData, setPdfData] = useState<string | null>(null)
  const [pdfNumPages, setPdfNumPages] = useState<number>(0)
  const [seed, setSeed] = useState<string | null>(null)
  const [hasInitialGenerated, setHasInitialGenerated] = useState(false)

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollPosRef = useRef<number>(0)

  const { mutate: createWorksheet, isPending: isSaving } =
    trpc.worksheets.createV2.useMutation({
      onSuccess(input) {
        captureWorksheetSaved({
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
          worksheet_id: input.id,
          source: "generator_save",
        })
        void downloadWorksheet(input.id, input.name + ".pdf", {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
          source: "generator_save",
        })
        toast("success", "Worksheet saved successfully")
      },
      onError(error, _variables, _context) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )
        toast(type, message)
      },
    })

  const { mutate: previewWorksheet, isPending: isPreviewLoading } =
    trpc.worksheets.previewV2.useMutation({
      onSuccess(data) {
        setResolvedModules(normalizePreviewPages(data.pages))
        setPdfData(data.pdf)
        setSeed(data.seed)
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )
        toast(type, message)
      },
    })

  const mergedConfiguration = useMemo(() => {
    const resolvedAsRecord: Record<string, Record<string, WorksheetModule>> = {}
    if (resolvedModules) {
      resolvedModules.forEach((page, pageIndex) => {
        resolvedAsRecord[pageIndex] = {}
        page.forEach((module, moduleIndex) => {
          resolvedAsRecord[pageIndex][moduleIndex] = module
        })
      })
    }

    const merged: Record<string, Record<string, WorksheetModule>> = {
      ...resolvedAsRecord,
    }
    Object.keys(formValues.moduleConfigurations).forEach((pageKey) => {
      merged[pageKey] = {
        ...merged[pageKey],
        ...formValues.moduleConfigurations[pageKey],
      }
    })

    return merged
  }, [resolvedModules, formValues.moduleConfigurations])

  const onSubmit = useCallback(() => {
    if (!formValid) {
      return
    }

    previewWorksheet({
      subject: "NORWEGIAN",
      name: formValues.name,
      gradeGroup: formValues.gradeGroup,
      languageTheme: formValues.languageTheme,
      theme: formValues.theme,
      pages: formValues.pages,
      sizeConfiguration: formValues.layout,
      allowedModuleTypes:
        formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
      moduleConfiguration: formValues.moduleConfigurations as any,
    })
  }, [previewWorksheet, formValues, formValid])

  // Initial generation
  useEffect(() => {
    if (!hasInitialGenerated) {
      onSubmit()
      setHasInitialGenerated(true)
    }
  }, [hasInitialGenerated, onSubmit])

  // Auto-regenerate when page count changes
  const previousPagesRef = useRef(formValues.pages)
  useEffect(() => {
    if (hasInitialGenerated && formValues.pages !== previousPagesRef.current) {
      previousPagesRef.current = formValues.pages
      onSubmit()
    }
  }, [formValues.pages, hasInitialGenerated, onSubmit])

  const onRegenerateModule = useCallback(
    (pageIndex: number, moduleIndex: number) => {
      if (!seed) return

      if (scrollAreaRef.current) {
        scrollPosRef.current = scrollAreaRef.current.scrollTop
      }

      const moduleSeed = Math.random().toString(36).substring(7)

      const currentModuleConfig =
        formValues.moduleConfigurations[pageIndex]?.[moduleIndex]

      const resolvedModule = resolvedModules?.[pageIndex]?.[moduleIndex]
      if (!resolvedModule) return

      // Create a new configuration map that includes ALL resolved modules
      const newModuleConfigurations: Record<
        string,
        Record<string, WorksheetModule>
      > = {
        ...formValues.moduleConfigurations,
      }

      // Populate with resolved modules if they aren't already in formValues
      resolvedModules?.forEach((page, pIdx) => {
        if (!newModuleConfigurations[pIdx]) {
          newModuleConfigurations[pIdx] = {}
        }
        page.forEach((mod, mIdx) => {
          if (!newModuleConfigurations[pIdx][mIdx]) {
            newModuleConfigurations[pIdx][mIdx] = mod
          }
        })
      })

      // Update the specific module we are regenerating
      newModuleConfigurations[pageIndex][moduleIndex] = {
        ...resolvedModule,
        ...currentModuleConfig,
        seed: moduleSeed,
      } as any

      updateForm({
        moduleConfigurations: newModuleConfigurations,
      })

      previewWorksheet({
        subject: "NORWEGIAN",
        name: formValues.name,
        gradeGroup: formValues.gradeGroup,
        languageTheme: formValues.languageTheme,
        theme: formValues.theme,
        pages: formValues.pages,
        sizeConfiguration: formValues.layout,
        allowedModuleTypes:
          formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
        moduleConfiguration: newModuleConfigurations as any,
        seed: seed,
      })
    },
    [seed, formValues, resolvedModules, updateForm, previewWorksheet],
  )

  const onSave = useCallback(() => {
    if (!formValid) {
      return
    }
    createWorksheet({
      subject: "NORWEGIAN",
      name: formValues.name,
      gradeGroup: formValues.gradeGroup,
      languageTheme: formValues.languageTheme,
      theme: formValues.theme,
      pages: formValues.pages,
      sizeConfiguration: formValues.layout,
      allowedModuleTypes:
        formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
      moduleConfiguration: mergedConfiguration as any,
      seed: seed || undefined,
    })
  }, [createWorksheet, formValues, formValid, mergedConfiguration, seed])

  const onGradeGroupChange = useCallback(
    (value: GradeGroup) => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: value,
        },
        "grade",
        formValues.gradeGroup,
        value,
      )
      updateForm({
        gradeGroup: value,
      })
    },
    [formValues.gradeGroup, updateForm],
  )

  const onLanguageThemeChange = useCallback(
    (value: LanguageTheme) => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
        },
        "language_theme",
        formValues.languageTheme,
        value,
      )
      formChangeHandler("languageTheme")(value)
    },
    [formChangeHandler, formValues.gradeGroup, formValues.languageTheme],
  )

  const onLayoutChange = useCallback(
    (value: "half" | "full") => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
        },
        "size",
        formValues.layout,
        value,
      )
      formChangeHandler("layout")(value)
    },
    [formChangeHandler, formValues.gradeGroup, formValues.layout],
  )

  const onTaskTypesChange = useCallback(
    (value: WorksheetGenericLanguageModule[]) => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
        },
        "difficulty",
        formValues.taskTypes,
        value,
      )
      formChangeHandler("taskTypes")(value)
    },
    [formChangeHandler, formValues.gradeGroup, formValues.taskTypes],
  )

  const onPagesChange = useCallback(
    (value: number) => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
        },
        "pages",
        formValues.pages,
        value,
      )
      formChangeHandler("pages")(value)
    },
    [formChangeHandler, formValues.gradeGroup, formValues.pages],
  )

  const onThemeChange = useCallback(
    (value: WorksheetTheme) => {
      captureWorksheetConfigChange(
        {
          subject: "NORWEGIAN",
          gradeGroup: formValues.gradeGroup,
        },
        "theme",
        formValues.theme,
        value,
      )
      formChangeHandler("theme")(value)
    },
    [formChangeHandler, formValues.gradeGroup, formValues.theme],
  )

  const downloadPdf = useCallback((name: string, base64data: string) => {
    const linkSource = `data:application/pdf;base64,${base64data}`
    const downloadLink = document.createElement("a")
    const fileName = name

    downloadLink.href = linkSource
    downloadLink.download = fileName
    downloadLink.click()
  }, [])

  return (
    <GeneratorWrapper>
      <Sidebar>
        <GeneratorMainFrame basePath="/generator" />
        <Form
          preventDefault
          onSubmit={submitHandler(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden min-h-0"
        >
          <SidebarContent data-tour-scroll-container>
            <div data-tour="general-settings">
              <FormField>
                <Label>{translations.name}</Label>
                <Input
                  value={formValues.name}
                  error={formErrors.name}
                  onChange={formChangeHandler("name")}
                  width="100%"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                    }
                  }}
                />
              </FormField>
              <FormField>
                <Label>{translations.grade}</Label>
                <GradeLevelGroupSelect
                  value={formValues.gradeGroup}
                  onChange={onGradeGroupChange}
                />
              </FormField>
              <FormField>
                <Label>{translations.languageTheme}</Label>
                <LanguageThemeSelect
                  value={formValues.languageTheme}
                  onChange={onLanguageThemeChange}
                  filterToThemes={
                    formValues.taskTypes.length === 1 &&
                    formValues.taskTypes[0] === "generic/readingTexts"
                      ? legalReadingTextThemes
                      : undefined
                  }
                />
              </FormField>
              <FormField>
                <Label>{translations.layout}</Label>
                <LayoutSelect
                  value={formValues.layout}
                  onChange={onLayoutChange}
                />
              </FormField>
              <FormField>
                <Label>{translations.taskTypes}</Label>
                <NorwegianTaskTypesSelect
                  value={formValues.taskTypes}
                  onChange={onTaskTypesChange}
                  width="100%"
                  error={formErrors.taskTypes}
                />
              </FormField>
              <FormField data-tour="pages-input">
                <Label>{translations.numberOfPages}</Label>
                <NumberInput
                  value={formValues.pages}
                  onChange={onPagesChange}
                  minValue={1}
                  maxValue={10}
                  width="100%"
                  showPlusMinusButtons={true}
                />
              </FormField>
              <FormField>
                <Label>{translations.theme}</Label>
                <ThemeSelect
                  value={formValues.theme}
                  onChange={onThemeChange}
                />
              </FormField>
              <FormField>
                <span data-tour="global-regenerate-button">
                  <Button
                    width="100%"
                    disabled={isPreviewLoading || !formValid}
                  >
                    {isPreviewLoading
                      ? translations.loading
                      : translations.regenerate}
                  </Button>
                </span>
              </FormField>
            </div>

            {pdfData && (
              <div data-tour="page-configurations">
                <GenericLanguagePageConfigurationList
                  numberOfPages={formValues.pages}
                  layout={formValues.layout}
                  pagesConfiguration={mergedConfiguration as any}
                  onPageModuleConfigurationChanged={(
                    page,
                    moduleNumber,
                    moduleConfiguration,
                  ) => {
                    updateForm({
                      moduleConfigurations: {
                        ...formValues.moduleConfigurations,
                        [page]: {
                          ...formValues.moduleConfigurations[page],
                          [moduleNumber]: moduleConfiguration,
                        },
                      },
                    })
                  }}
                  onRegenerateModule={onRegenerateModule}
                />
              </div>
            )}
          </SidebarContent>
        </Form>
      </Sidebar>

      <PreviewArea data-tour="preview-area">
        {pdfData && (
          <>
            <FloatingActions>
              <span data-tour="download-button">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    if (pdfData) {
                      captureWorksheetDownload({
                        subject: "NORWEGIAN",
                        gradeGroup: formValues.gradeGroup,
                        source: "generator_preview",
                      })
                      downloadPdf(formValues.name + ".pdf", pdfData)
                    }
                  }}
                  title={translations.download}
                >
                  <SVGIcon icon="Download" className="w-4 h-4" />
                </Button>
              </span>
              <span data-tour="save-button">
                <Button
                  size="small"
                  variant="secondary"
                  disabled={isSaving || !formValid}
                  onClick={onSave}
                  title={translations.save}
                >
                  {isSaving ? (
                    translations.loading
                  ) : (
                    <>
                      <SVGIcon icon="Check" className="w-4 h-4 mr-2" />
                      {translations.save}
                    </>
                  )}
                </Button>
              </span>
            </FloatingActions>
            <PdfScrollArea ref={scrollAreaRef}>
              <Document
                file={`data:application/pdf;base64,${pdfData}`}
                className="flex flex-col gap-4"
                onLoadSuccess={({ numPages }) => {
                  setPdfNumPages(numPages)
                  if (scrollAreaRef.current) {
                    scrollAreaRef.current.scrollTop = scrollPosRef.current
                  }
                }}
              >
                {Array.from(new Array(pdfNumPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={600}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => {
                      if (index === 0 && scrollAreaRef.current) {
                        scrollAreaRef.current.scrollTop = scrollPosRef.current
                      }
                    }}
                  />
                ))}
              </Document>
            </PdfScrollArea>
          </>
        )}
        {!pdfData && isPreviewLoading && (
          <div className="flex items-center justify-center h-full text-gray-400">
            {translations.loading}
          </div>
        )}
      </PreviewArea>
    </GeneratorWrapper>
  )
}

export default NorwegianGenerator
